import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import IconButton from '@components/dataEntry/button/IconButton';
import { useRecoilState } from 'recoil';
import { notificationOpenState } from '@atom/setting/jukebox';
import DraggableWrapper from '@components/wrapper/DraggableWrapper';
import { DragContainer } from '@components/wrapper/DragContainer';
import Notifications from './Notifications';
import useWindowWidth from '@hooks/useWindowWidth';

const MIN_HEIGHT = 200;
const MAX_HEIGHT =
  typeof window !== 'undefined' ? window.innerHeight * 0.9 : 800;
const DEFAULT_HEIGHT = 600;
const PANEL_WIDTH = 384;

// ── 드래거블 모드 스타일 ──────────────────────────────────────
const Header = styled.header<{ $draggable?: boolean }>`
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 50px;
  padding: 0 12px;
  cursor: ${({ $draggable }) => ($draggable ? 'move' : 'default')};
`;

const HeaderBtns = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ResizeHandle = styled.div`
  flex-shrink: 0;
  width: 100%;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: row-resize;
  border-radius: 0 0 16px 16px;
  user-select: none;
  transition: background-color 0.15s;

  &:hover {
    background-color: var(--inkColor);
  }

  &::before {
    content: '';
    width: 32px;
    height: 4px;
    border-radius: 2px;
    background: var(--lineColor);
    opacity: 0.6;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
`;

// ── 확장 패널 모드 스타일 ─────────────────────────────────────
const ExpandedPanel = styled(motion.div)`
  position: fixed;
  right: 0;
  top: 0;
  height: 100vh;
  height: 100dvh;
  width: ${PANEL_WIDTH}px;
  background: var(--backgroundColor);
  backdrop-filter: blur(24px);
  border-left: 1px solid var(--lineColor);
  z-index: 200;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;

  @media (max-width: 480px) {
    width: 100%;
    border-left: none;
    border-top: 1px solid var(--lineColor);
    height: 85dvh;
    top: auto;
    bottom: 0;
    border-radius: 16px 16px 0 0;
  }
`;

const slideVariants = {
  hidden: { x: '100%' },
  visible: {
    x: 0,
    transition: { type: 'spring' as const, damping: 28, stiffness: 280 },
  },
  exit: { x: '100%', transition: { duration: 0.22, ease: 'easeInOut' } },
};

type Props = {
  zIndex?: number;
  onActivate?: () => void;
};

function NotificationDraggable({ zIndex, onActivate }: Props) {
  const [open, setOpen] = useRecoilState(notificationOpenState);
  const ww = useWindowWidth();
  const isMobile = (ww || 0) <= 480;
  const [isExpanded, setIsExpanded] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth <= 480
      ? true
      : localStorage.getItem('noti_panel_expanded') === 'true',
  );
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const boxRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(DEFAULT_HEIGHT);

  const close = useCallback(() => setOpen(false), [setOpen]);
  const toggleExpand = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (isMobile) return;
      setIsExpanded((prev) => {
        const next = !prev;
        localStorage.setItem('noti_panel_expanded', String(next));
        return next;
      });
    },
    [isMobile],
  );

  useEffect(() => {
    if (isMobile && !isExpanded) {
      setIsExpanded(true);
    }
  }, [isMobile, isExpanded]);

  // 리사이즈 시작 (마우스 / 터치 공용)
  const startResize = useCallback(
    (clientY: number) => {
      isResizing.current = true;
      startY.current = clientY;
      startHeight.current = height;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'row-resize';
    },
    [height],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      startResize(e.clientY);
    },
    [startResize],
  );

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();
      startResize(e.touches[0].clientY);
    },
    [startResize],
  );

  useEffect(() => {
    const onMove = (clientY: number) => {
      if (!isResizing.current) return;
      const delta = clientY - startY.current;
      const next = Math.min(
        MAX_HEIGHT,
        Math.max(MIN_HEIGHT, startHeight.current + delta),
      );
      setHeight(next);
    };

    const onEnd = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    const handleMouseMove = (e: MouseEvent) => onMove(e.clientY);
    const handleTouchMove = (e: TouchEvent) => {
      if (isResizing.current) e.preventDefault();
      onMove(e.touches[0].clientY);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', onEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, []);

  const headerBtns = (expanded: boolean) => (
    <HeaderBtns>
      {!isMobile && (
        <IconButton
          size="md"
          shape="default"
          icon={expanded ? 'PanelCollapse14' : 'PanelExpand14'}
          onClick={toggleExpand}
          tooltip={expanded ? '드래그 모드로 전환' : '패널 확장'}
          tooltipDir="bottom"
        />
      )}
      <CloseBtn onClick={close} size="md" shape="default" />
    </HeaderBtns>
  );

  return (
    <>
      {/* ── 드래거블 모드 ── */}
      {!isExpanded && open && (
        <DraggableWrapper direction="bottomRight" onActivate={onActivate}>
          <DragContainer
            floatingDir="bottom"
            width={PANEL_WIDTH}
            ref={boxRef}
            onMouseDownCapture={onActivate}
            onTouchStartCapture={onActivate}
            style={{
              zIndex,
              height,
              minHeight: MIN_HEIGHT,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              padding: 0,
            }}
          >
            <Header $draggable>
              <h6>알림</h6>
              {headerBtns(false)}
            </Header>

            <ContentWrapper>
              <Notifications />
            </ContentWrapper>

            <ResizeHandle
              onMouseDown={onMouseDown}
              onTouchStart={onTouchStart}
            />
          </DragContainer>
        </DraggableWrapper>
      )}

      {/* ── 확장 패널 모드 ── */}
      <AnimatePresence>
        {isExpanded && open && (
          <>
            <ExpandedPanel
              key="panel"
              variants={slideVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onMouseDownCapture={onActivate}
              onTouchStartCapture={onActivate}
              style={{ zIndex }}
            >
              <Header>
                <h6>알림</h6>
                {headerBtns(true)}
              </Header>

              <ContentWrapper>
                <Notifications />
              </ContentWrapper>
            </ExpandedPanel>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default NotificationDraggable;
